/**
 * vehicleSyncService.ts
 *
 * 車両差分F（FEベース車両一覧差分アップデート）サービス
 *
 * バックエンドの差分APIがない状態で、フロント側で車両データの変更を検出・反映する。
 * - 30秒ごとに全車両データを取得（テスト用）
 * - キャッシュと比較して差分を検出
 * - 追加・更新・削除の変更をコールバックで通知
 */

import { fetchDispatchVehicles } from '../api/dispatchService';
import { debug } from '../lib/debug';
import { SYNC_INTERVAL_MS } from '../constants/calendar';
import type { Vehicle } from '../types';

/** 車両フィールドの日本語ラベル */
const VEHICLE_FIELD_LABELS: Record<string, string> = {
  vehicle_code: '車両コード',
  vehicle_number: '車両番号',
  status: 'ステータス',
  completion: '整備状況',
  active: '稼働状態',
  active_from: '稼働開始日',
  active_to: '稼働終了日',
  cleaning: '清掃',
  car_wash: '洗車',
  check1: 'チェック1',
  check2: 'チェック2',
  tire_groove: 'タイヤ溝',
  tire_pressure: 'タイヤ空気圧',
  memo: 'メモ',
  remarks: '備考',
  priority: '並び順',
  out_of_use: '使用不可',
};

type SyncChange = {
  type: string;
  entityType?: string;
  id?: any;
  oldData?: any;
  newData?: any;
  changedFields?: any[];
};

type SyncCallback = (changes: SyncChange[], allVehicles: Vehicle[]) => void;

interface SyncStatus {
  isRunning: boolean;
  cachedCount: number;
  lastSyncTime: string | null;
}

const SYNC_INTERVAL = SYNC_INTERVAL_MS; // 統一された同期インターバル

class VehicleSyncService {
  intervalId: ReturnType<typeof setInterval> | null;
  cachedVehicles: Map<any, Vehicle>;
  listeners: Set<SyncCallback>;
  isRunning: boolean;
  lastSyncTime: string | null;
  syncCount: number;
  isFirstSync: boolean;

  constructor() {
    this.intervalId = null;
    this.cachedVehicles = new Map(); // id -> vehicle
    this.listeners = new Set();
    this.isRunning = false;
    this.lastSyncTime = null;
    this.syncCount = 0; // 同期回数カウント
    this.isFirstSync = true; // 初回同期フラグ（変更検出をスキップ）
  }

  /**
   * リスナー登録
   */
  subscribe(callback: any): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * 同期を開始
   */
  start(onChanges?: SyncCallback): void {
    if (onChanges) {
      this.subscribe(onChanges);
    }

    if (this.isRunning) {
      return;
    }

    this.isRunning = true;
    this.syncCount = 0;

    this.sync();

    this.intervalId = setInterval(() => {
      this.sync();
    }, SYNC_INTERVAL);
  }

  /** 初回データ取得のみ（ポーリングなし） */
  startOnce(onChanges?: SyncCallback): void {
    if (onChanges) {
      this.subscribe(onChanges);
    }
    if (this.isRunning) return;
    this.isRunning = true;
    this.syncCount = 0;
    this.sync();
  }

  /** ポーリング開始 */
  startPolling(): void {
    if (this.intervalId) return;
    this.intervalId = setInterval(() => {
      this.sync();
    }, SYNC_INTERVAL);
    console.log('[VehicleSync] Polling started');
  }

  /** ポーリング停止（キャッシュ・リスナーは維持） */
  stopPolling(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('[VehicleSync] Polling stopped');
    }
  }

  /**
   * 同期を停止
   */
  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.isRunning = false;
    // キャッシュは保持する
  }

  /**
   * 強制同期（インターバルもリセット）
   * Promise を返すため、呼び出し元で完了を待機可能。
   */
  forceSync(): Promise<void> {
    // ポーリング状態を維持したまま即時同期
    return this.sync();
  }

  /**
   * 現在のキャッシュを取得
   */
  getVehicles(): Vehicle[] {
    return Array.from(this.cachedVehicles.values());
  }

  /**
   * 特定の車両をキャッシュから取得（キーの number/string 揺れを吸収）。
   * キャッシュキーは fetch 時の v.id（通常 number）だが、Pusher 経路等で
   * String(id) 参照されるとミスするため、文字列比較でフォールバックする。
   */
  getVehicle(id: any): Vehicle | undefined {
    if (id == null) return undefined;
    const direct = this.cachedVehicles.get(id);
    if (direct) return direct;
    for (const k of this.cachedVehicles.keys()) {
      if (String(k) === String(id)) return this.cachedVehicles.get(k);
    }
    return undefined;
  }

  /**
   * キャッシュにデータがあるか
   */
  hasData(): boolean {
    return this.cachedVehicles.size > 0;
  }

  notifyListeners(changes: SyncChange[], vehicles: Vehicle[]): void {
    this.listeners.forEach(callback => {
      try {
        callback(changes, vehicles);
      } catch (e) {
        console.error('[VehicleSync] Listener error:', e);
      }
    });
  }

  /**
   * 同期実行
   */
  async sync(): Promise<void> {
    try {
      this.syncCount++;
      const syncId = this.syncCount;
      const startTime = Date.now();

      const vehicles = await fetchDispatchVehicles();

      const elapsed = Date.now() - startTime;
      // 初回同期: キャッシュ初期化 + リスナーに全件通知
      // ロード直後のデータが「追加」として変更履歴に記録されないよう type は initial_load
      if (this.isFirstSync) {
        this.updateCache(vehicles);
        this.lastSyncTime = new Date().toISOString();
        this.isFirstSync = false;
        this.notifyListeners([{ type: 'initial_load', entityType: 'vehicle' }], vehicles);
        return;
      }

      const changes = this.detectChanges(vehicles);

      if (changes.length > 0) {
        console.log(`[VehicleSync] #${syncId} ${changes.length}件の変更を検出:`,
          changes.slice(0, 3).map(c => `${c.type}:${c.id}`).join(', ')
        );
        this.updateCache(vehicles);
        this.lastSyncTime = new Date().toISOString();

        // コールバック呼び出し
        this.notifyListeners(changes, vehicles);
      } else {
        this.lastSyncTime = new Date().toISOString();
      }

      // ユーザー要望: 更新完了をコンソールに出力
      const ts = new Date().toLocaleString('sv-SE').replace(' ', 'T');
      console.log(`[VehicleSync] updated(${ts})`);
    } catch (error) {
      console.error('[VehicleSync] ❌ 同期エラー:', error);
      // 初回同期が失敗した場合、次回 startOnce() でやり直せるよう状態をリセット
      if (this.isFirstSync) {
        this.isRunning = false;
      }
    }
  }

  /**
   * 差分検出
   */
  detectChanges(newVehicles: Vehicle[]): SyncChange[] {
    const changes: SyncChange[] = [];
    const newVehicleMap = new Map(newVehicles.map(v => [v.id, v]));

    // 更新・削除を検出
    for (const [id, cached] of this.cachedVehicles) {
      const newVehicle = newVehicleMap.get(id);

      if (!newVehicle) {
        // 削除
        changes.push({
          type: 'delete',
          entityType: 'vehicle',
          id,
          oldData: cached,
          newData: null,
        });
      } else if (this.hasChanged(cached, newVehicle)) {
        // 更新
        changes.push({
          type: 'update',
          entityType: 'vehicle',
          id,
          oldData: cached,
          newData: newVehicle,
          changedFields: this.getChangedFields(cached, newVehicle),
        });
      }
    }

    // 追加を検出
    for (const [id, vehicle] of newVehicleMap) {
      if (!this.cachedVehicles.has(id)) {
        changes.push({
          type: 'create',
          entityType: 'vehicle',
          id,
          oldData: null,
          newData: vehicle,
        });
      }
    }

    return changes;
  }

  /**
   * 変更有無チェック
   */
  hasChanged(oldVehicle: Vehicle, newVehicle: Vehicle): boolean {
    const oldAt = (oldVehicle as any).updatedAt || (oldVehicle as any).updated_at;
    const newAt = (newVehicle as any).updatedAt || (newVehicle as any).updated_at;

    if (oldAt && newAt) {
      const oldTime = new Date(oldAt).getTime();
      const newTime = new Date(newAt).getTime();
      if (!isNaN(oldTime) && !isNaN(newTime)) {
        return oldTime < newTime;
      }
    }
    // なければJSON比較（フォールバック）
    return JSON.stringify(oldVehicle) !== JSON.stringify(newVehicle);
  }

  /**
   * 変更フィールド取得
   */
  getChangedFields(oldVehicle: Vehicle, newVehicle: Vehicle): any[] {
    const changed: any[] = [];
    const allKeys = new Set([...Object.keys(oldVehicle), ...Object.keys(newVehicle)]);

    // スキップするフィールド（内部用・派生・ノイズが多いもの）
    const skipFields = new Set([
      '_', 'updated_at', 'updatedAt', 'created_at', 'createdAt',
      'status_name', 'status_code', // status から派生
      'last_checked_at', // タイムスタンプ（ノイズ）
      'check_situations', // オブジェクト配列（表示に適さない）
      'vehicle_type', // ネストされたオブジェクト
      'resourceId', 'resource_id', 'vehicleId', 'vehicle_id', // 重複ID
      'bg_color', 'text_color', 'color1', 'color', // 色情報（内部用）
      'car_type', 'car_type_id', 'car_type_code', // vehicle_type から派生
      'vehicle_type_id', 'vehicle_type_page_id', 'vehicle_type_page_ids', // 内部用
    ]);

    for (const key of allKeys) {
      if (key.startsWith('_') || skipFields.has(key)) continue;

      const oldVal = (oldVehicle as any)[key];
      const newVal = (newVehicle as any)[key];

      // 空値同士の変更はスキップ
      const isOldEmpty = oldVal === null || oldVal === undefined || oldVal === '';
      const isNewEmpty = newVal === null || newVal === undefined || newVal === '';
      if (isOldEmpty && isNewEmpty) continue;

      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        changed.push({
          field: key,
          label: VEHICLE_FIELD_LABELS[key] || key,
          oldValue: oldVal,
          newValue: newVal,
        });
      }
    }
    return changed;
  }

  /**
   * キャッシュ更新
   */
  updateCache(vehicles: Vehicle[]): void {
    this.cachedVehicles.clear();
    for (const v of vehicles) {
      this.cachedVehicles.set(v.id, v);
    }
  }

  /**
   * ローカル操作の保存結果をキャッシュへ即時反映する。
   *
   * 車両ポジションのDnD等で updateVehicle が成功した際、そのレスポンス（サーバ確定値）を
   * ポーリング（30秒）や Pusher エコーを待たずにキャッシュ＆リスナーへ反映する。
   * Pusher 無効の宮古では楽観的更新（5秒）解除後に旧データへ戻る問題の対策として必須。
   * 那覇では後続の Pusher エコーが updated_at 比較（<= はスキップ）で重複排除される。
   */
  applyLocalUpdate(vehicle: Vehicle | null | undefined): void {
    if (!vehicle || (vehicle as any).id == null) return;
    // キャッシュキーは fetch 時の v.id をそのまま使っており number/string が揺れる可能性が
    // あるため、文字列比較で既存キーを特定してから上書きする（重複エントリ防止）。
    let key: any = (vehicle as any).id;
    let existing = this.cachedVehicles.get(key);
    if (!existing) {
      for (const k of this.cachedVehicles.keys()) {
        if (String(k) === String((vehicle as any).id)) {
          key = k;
          existing = this.cachedVehicles.get(k);
          break;
        }
      }
    }
    // レスポンスが部分的でも既存フィールドを失わないようマージ
    const merged = existing ? { ...existing, ...vehicle } : vehicle;
    this.cachedVehicles.set(key, merged as Vehicle);
    this.notifyListeners(
      [{ type: 'local_update', entityType: 'vehicle', id: (vehicle as any).id, oldData: existing || null, newData: merged }],
      this.getVehicles(),
    );
    debug.log('vehicleSyncService', 'applyLocalUpdate', (vehicle as any).id);
  }

  /**
   * 初期キャッシュ設定（既存データから）
   * 外部から既にロード済みの車両データでキャッシュを初期化する
   */
  initializeCache(vehicles: Vehicle[]): void {
    if (!Array.isArray(vehicles)) {
      debug.warn('vehicleSyncService', 'initializeCache called with non-array:', vehicles);
      return;
    }
    this.updateCache(vehicles);
    debug.log('vehicleSyncService', 'Cache initialized from external data');
  }

  /**
   * 状態取得
   */
  getStatus(): SyncStatus {
    return {
      isRunning: this.isRunning,
      cachedCount: this.cachedVehicles.size,
      lastSyncTime: this.lastSyncTime,
    };
  }
}

// シングルトンインスタンス
export const vehicleSyncService = new VehicleSyncService();
